import { Building2, Check, Users } from "lucide-react";
import React, { useState } from "react";
import { cn } from "src/common/utils";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { Textarea } from "src/components/ui/textarea";

interface ExistingTeam {
  id: string;
  name: string;
}

interface TeamStepProps {
  onCreateTeam: (data: { name: string; description: string }) => void;
  onSkip: () => void;
  onContinue: () => void;
  isLoading?: boolean;
  productName: string;
  existingTeams?: ExistingTeam[];
  onCancel?: () => void;
}

type TeamChoice = "one-team" | "specific-teams" | null;

export const TeamStep: React.FC<TeamStepProps> = ({
  onCreateTeam,
  onSkip,
  onContinue,
  isLoading,
  productName,
  existingTeams = [],
  onCancel,
}) => {
  const [choice, setChoice] = useState<TeamChoice>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const hasExistingTeams = existingTeams.length > 0;

  // User chose "continue" - proceed immediately
  React.useEffect(() => {
    if (choice === "one-team") {
      onSkip();
    }
  }, [choice, onSkip]);

  const handleSubmit = () => {
    onCreateTeam({ name, description });
  };

  const isValid = name.trim().length > 0;

  // If teams already exist, show them and allow continuing
  if (hasExistingTeams) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Team created</h2>
          <p className="text-muted-foreground mt-2">
            You can add more teams later from Settings.
          </p>
        </div>

        <div className="space-y-3">
          {existingTeams.map(team => (
            <div
              key={team.id}
              className="flex items-center gap-3 p-4 rounded-lg bg-card/50 border border-border"
            >
              <div className="p-2 rounded-lg bg-green-500/20">
                <Check className="w-4 h-4 text-green-400" />
              </div>
              <span className="text-foreground font-medium">{team.name}</span>
            </div>
          ))}
        </div>

        <div className="flex justify-between pt-4">
          {onCancel ? (
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          ) : <div />}
          <Button onClick={onContinue} disabled={isLoading}>
            Continue
          </Button>
        </div>
      </div>
    );
  }

  // Show card selection if no choice made yet
  if (choice === null) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Set up a team?</h2>
          <p className="text-muted-foreground mt-2">
            Teams let you customize {productName} for different groups with their own context and agents.
            You can always add teams later.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Skip Option - Default/Recommended */}
          <button
            onClick={() => setChoice("one-team")}
            className={cn(
              "flex flex-col items-start p-5 rounded-lg border-2 text-left transition-all",
              "border-primary/50 hover:border-primary bg-card/50 hover:bg-card"
            )}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-accent">
                <Building2 className="w-5 h-5 text-muted-foreground" />
              </div>
              <span className="text-lg font-medium text-foreground">Skip for now</span>
              <span className="text-xs font-medium text-primary bg-primary/20 px-2 py-0.5 rounded">
                Recommended
              </span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              We'll create a "General" team for everyone using your company context.
            </p>
            <span className="text-xs text-muted-foreground bg-accent/50 px-2 py-1 rounded">
              Best for quick setup and shared context
            </span>
          </button>

          {/* Add Team Option */}
          <button
            onClick={() => setChoice("specific-teams")}
            className={cn(
              "flex flex-col items-start p-5 rounded-lg border-2 text-left transition-all",
              "border-border hover:border-border bg-card/50 hover:bg-card"
            )}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-accent">
                <Users className="w-5 h-5 text-muted-foreground" />
              </div>
              <span className="text-lg font-medium text-foreground">Yes, let's do it</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Create a team for your group with its own context.
            </p>
            <span className="text-xs text-muted-foreground bg-accent/50 px-2 py-1 rounded">
              Best when onboarding many teams with distinct alerts and context
            </span>
          </button>
        </div>
      </div>
    );
  }

  if (choice === "one-team") {
    return null;
  }

  // User chose "specific teams" - show the form
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">Create your first team</h2>
        <p className="text-muted-foreground mt-2">
          You can add more teams later from Settings.
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="teamName">Team Name</Label>
          <Input
            id="teamName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Photos Team"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="teamDescription">Description</Label>
          <p className="text-sm text-muted-foreground">
            Help {productName} understand what this team owns so it can route issues appropriately.
          </p>
          <Textarea
            id="teamDescription"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., Owns the photo sharing product, as well as the photos-backend service, and frontend/photos-ux repo"
            rows={3}
          />
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="ghost" onClick={() => setChoice(null)} disabled={isLoading}>
          Back
        </Button>
        <Button onClick={handleSubmit} disabled={!isValid || isLoading}>
          {isLoading ? "Creating..." : "Create Team"}
        </Button>
      </div>
    </div>
  );
};
